import React, { Component } from "react";
import MutextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import {
  connectStripe,
  verifyStripeKeys,
  updateStripeKeys,
  testStripeConnection,
  disconnectStripe,
  saveStripeSettings,
  saveStripeWebhookSecret,
  checkStripeWebhook,
  getStripeDeliveries,
} from "../../../config/reseller_api_calls";
import "./StripeModal.css";

/*
 * Stripe configuration modal.
 *
 * Behaviour follows new-obs-mockups/obs-admin-phase3-integrations.html (#mSk);
 * the presentation uses the portal's own DashLite components (modal-header with
 * icon plate, nav-tabs, MutextField, custom-switch, alert-pro, btn-primary), so
 * it reads as part of the admin portal rather than as a piece of Stripe
 * embedded in it — the same treatment as the SIPNav modals above it.
 *
 * Three facts are kept apart, because collapsing them loses something:
 *   connected  a key pair is held
 *   mode       which key pair is in use, and so whether money moves
 *   enabled    whether clients can pay at all (pause without disconnecting)
 *
 * Opened/closed by the parent with window.$("#modalStripeIntegration").modal().
 * Props: status (from /integrations/stripe/status), accountno, token,
 * onStatusChange(status), toast(message, type).
 */

const METHODS = [
  { k: "card", n: "Credit / debit card", s: "Visa, Mastercard, Amex, Discover. Always on.", locked: true },
  { k: "ach", n: "ACH bank debit", s: "US accounts. Three to five days to clear, and cheaper than a card." },
  { k: "link", n: "Link", s: "Stripe's saved-details checkout." },
  { k: "wallet", n: "Apple Pay / Google Pay", s: "Shown by itself on a device that supports it. Needs the portal domain registered in your Stripe dashboard." },
  { k: "sepa", n: "SEPA direct debit", s: "Euro bank accounts." },
  { k: "bacs", n: "Bacs direct debit", s: "UK bank accounts." },
];
const WHERE = [
  { k: "invoices", n: "On invoices", s: "A Pay button on any unpaid invoice in the client portal." },
  { k: "quotes", n: "At quotation acceptance", s: "Take payment the moment a client accepts a quotation. Coming in a later release." },
  { k: "autopay", n: "Stored for auto-charge", s: "Lets a client keep a card on file when paying. Charging on the due date comes in a later release." },
];
const CURRENCIES = [
  { v: "USD", t: "USD — US Dollar" },
  { v: "CAD", t: "CAD — Canadian Dollar" },
  { v: "GBP", t: "GBP — British Pound" },
  { v: "EUR", t: "EUR — Euro" },
];

const EMPTY_PAIR = { held: false, publishable_key: null, secret_key_mask: null, account_id: null, business_name: "", verified_at: null };

function ago(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return mins + " min ago";
  const h = Math.round(mins / 60);
  if (h < 24) return h + " hour" + (h === 1 ? "" : "s") + " ago";
  const days = Math.round(h / 24);
  if (days === 1) return "Yesterday";
  if (days < 30) return days + " days ago";
  return d.toLocaleDateString();
}

class StripeModal extends Component {
  constructor(props) {
    super(props);
    this.state = this.freshState(props.status);
  }

  freshState(status) {
    const s = status || {};
    return {
      tab: "conn",
      busy: "",
      error: "",
      success: "",
      // connect card
      cpk: "",
      csk: "",
      cpkErr: "",
      cskErr: "",
      connOut: null,
      // update keys card
      updOpen: false,
      updTarget: s.mode || "test",
      newPk: "",
      newSk: "",
      newPkErr: "",
      newSkErr: "",
      verified: false,
      verifyOut: null,
      testOut: null,
      // settings
      moreMethods: false,
      moreWhere: false,
      methods: Object.assign({ card: true }, s.methods || {}),
      where: Object.assign({ invoices: true }, s.where || {}),
      autoCustomer: s.auto_create_customer !== false,
      descriptor: s.statement_descriptor || "",
      descErr: false,
      currency: s.currency || "USD",
      // webhooks
      whInput: "",
      whErr: false,
      whReplace: false,
      pingOut: null,
      events: (s.webhook && s.webhook.events) || [],
      deliveries: [],
      deliveriesLoaded: false,
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.status !== this.props.status && this.props.status) {
      const s = this.props.status;
      // Server state won; refresh the editable copies without dropping tab or open panels.
      this.setState({
        methods: Object.assign({ card: true }, s.methods || {}),
        where: Object.assign({ invoices: true }, s.where || {}),
        autoCustomer: s.auto_create_customer !== false,
        descriptor: s.statement_descriptor || "",
        currency: s.currency || "USD",
        events: (s.webhook && s.webhook.events) || [],
        updTarget: this.state.updOpen ? this.state.updTarget : s.mode || "test",
      });
    }
  }

  /** Parent calls this right before showing the modal. */
  open = () => {
    this.setState(this.freshState(this.props.status));
  };

  // ── helpers ────────────────────────────────────────────────────────────────

  s() {
    return this.props.status || { connected: false, enabled: false, mode: "test", test: EMPTY_PAIR, live: EMPTY_PAIR, webhook: {} };
  }
  pair(mode) {
    return this.s()[mode] || EMPTY_PAIR;
  }
  has(mode) {
    return !!this.pair(mode).held;
  }
  biz() {
    const s = this.s();
    return this.pair(s.mode).business_name || "your Stripe account";
  }

  async call(name, fn) {
    this.setState({ busy: name, error: "", success: "" });
    try {
      const res = await fn();
      const payload = res && res.data;
      if (!payload) throw new Error("No response from the server.");
      if (payload.status === 403 || payload.errors === "authentication missing" || payload.errors === "jwt expired") {
        throw new Error("Your session has expired. Sign in again.");
      }
      if (payload.status !== 200) {
        const msg = payload.message || (payload.errors && payload.errors[0] && payload.errors[0].msg) || "Request failed.";
        throw new Error(msg);
      }
      this.setState({ busy: "" });
      return payload;
    } catch (e) {
      this.setState({ busy: "", error: e.message || "Request failed." });
      return null;
    }
  }

  applyStatus(payload, toastMsg) {
    if (payload && payload.data && this.props.onStatusChange) this.props.onStatusChange(payload.data);
    if (toastMsg && this.props.toast) this.props.toast(toastMsg, "success");
  }

  checkPair(pk, sk, mode) {
    const want = mode === "live" ? "_live_" : "_test_";
    const pkOk = pk.indexOf("pk" + want) === 0;
    const skOk = sk.indexOf("sk" + want) === 0 || sk.indexOf("rk" + want) === 0;
    return {
      ok: pkOk && skOk,
      pkErr: pkOk ? "" : !pk ? "Enter the publishable key." : `A ${mode} publishable key starts pk${want}.`,
      skErr: skOk ? "" : !sk ? "Enter the secret key." : `A ${mode} secret key starts sk${want}.`,
    };
  }

  // ── connection actions ─────────────────────────────────────────────────────

  connect = async () => {
    const pk = this.state.cpk.trim();
    const sk = this.state.csk.trim();
    const mode = this.s().mode || "test";
    const c = this.checkPair(pk, sk, mode);
    this.setState({ cpkErr: c.pkErr, cskErr: c.skErr, connOut: null });
    if (!c.ok) return;
    const payload = await this.call("connect", () => connectStripe(this.props.accountno, this.props.token, pk, sk, mode));
    if (!payload) {
      this.setState({ connOut: { ok: false, text: this.state.error } });
      return;
    }
    this.setState({ cpk: "", csk: "", connOut: { ok: true, text: "Connected." } });
    this.applyStatus(payload, `Connected to Stripe with the ${mode} keys`);
  };

  setMode = async (m) => {
    const s = this.s();
    if (m === s.mode) return;
    if (m === "live" && !this.has("live")) {
      this.openUpdate("live");
      return;
    }
    const payload = await this.call("mode", () => saveStripeSettings(this.props.accountno, this.props.token, { mode: m }));
    if (!payload) return;
    this.setState({ updOpen: false, verified: false });
    this.applyStatus(payload, m === "live" ? "Live — clients are now charged for real" : "Back on test keys — no money moves");
  };

  openUpdate = (target) => {
    this.setState({
      updOpen: true,
      updTarget: target || this.s().mode || "test",
      newPk: "",
      newSk: "",
      newPkErr: "",
      newSkErr: "",
      verified: false,
      verifyOut: null,
    });
  };
  closeUpdate = () => this.setState({ updOpen: false, verified: false, verifyOut: null, newPkErr: "", newSkErr: "" });

  verify = async () => {
    const pk = this.state.newPk.trim();
    const sk = this.state.newSk.trim();
    const t = this.state.updTarget;
    const c = this.checkPair(pk, sk, t);
    this.setState({ newPkErr: c.pkErr, newSkErr: c.skErr, verified: false });
    if (!c.ok) {
      this.setState({ verifyOut: { ok: false, text: `Not a valid ${t} pair.` } });
      return;
    }
    const payload = await this.call("verify", () => verifyStripeKeys(this.props.accountno, this.props.token, pk, sk, t));
    if (!payload) {
      this.setState({ verifyOut: { ok: false, text: this.state.error } });
      return;
    }
    const name = (payload.data && payload.data.business_name) || "Stripe";
    this.setState({ verified: true, verifyOut: { ok: true, text: `Verified — reached ${name}.` } });
  };

  applyKeys = async () => {
    if (!this.state.verified) return;
    const t = this.state.updTarget;
    const switchMode = t !== this.s().mode;
    const payload = await this.call("update", () =>
      updateStripeKeys(this.props.accountno, this.props.token, this.state.newPk.trim(), this.state.newSk.trim(), t, switchMode)
    );
    if (!payload) return;
    this.setState({ updOpen: false, verified: false, newPk: "", newSk: "" });
    this.applyStatus(payload, switchMode && t === "live" ? "Live — clients are now charged for real" : `The ${t} keys are in place`);
  };

  test = async () => {
    const mode = this.s().mode;
    if (!this.has(mode)) {
      this.setState({ testOut: { ok: false, text: `No ${mode} keys held.` } });
      return;
    }
    const payload = await this.call("test", () => testStripeConnection(this.props.accountno, this.props.token, mode));
    if (!payload) {
      this.setState({ testOut: { ok: false, text: this.state.error } });
      return;
    }
    const name = (payload.data && payload.data.business_name) || "Stripe";
    this.setState({ testOut: { ok: true, text: `Reached ${name}.` } });
  };

  disconnect = async () => {
    if (!window.confirm("Disconnect Stripe? Clients will no longer be able to pay by card, and the stored keys are removed.")) return;
    const payload = await this.call("disconnect", () => disconnectStripe(this.props.accountno, this.props.token));
    if (!payload) return;
    this.applyStatus(payload, "Stripe disconnected — clients can no longer pay by card");
    this.close();
  };

  // ── settings actions ───────────────────────────────────────────────────────

  toggleEnabled = async () => {
    const next = !this.s().enabled;
    const payload = await this.call("enabled", () => saveStripeSettings(this.props.accountno, this.props.token, { enabled: next ? 1 : 0 }));
    if (!payload) return;
    this.applyStatus(payload, next ? "Clients can pay again" : "Payments paused — the Pay button is hidden");
  };

  flipMethod = (k) => {
    if (k === "card") {
      if (this.props.toast) this.props.toast("Cards are always accepted", "info");
      return;
    }
    this.setState({ methods: Object.assign({}, this.state.methods, { [k]: !this.state.methods[k] }) });
  };
  flipWhere = (k) => this.setState({ where: Object.assign({}, this.state.where, { [k]: !this.state.where[k] }) });
  flipEvent = (k) => {
    const on = this.state.events.includes(k);
    this.setState({ events: on ? this.state.events.filter((x) => x !== k) : this.state.events.concat([k]) });
  };

  save = async () => {
    const wh = this.state.whInput.trim();
    if (wh && wh.indexOf("whsec_") !== 0) {
      this.setState({ whErr: true, tab: "hooks" });
      return;
    }
    const desc = this.state.descriptor.trim();
    if (!desc && this.s().connected) {
      this.setState({ descErr: true, tab: "settings" });
      return;
    }
    const body = {
      auto_create_customer: this.state.autoCustomer ? 1 : 0,
      statement_descriptor: desc,
      currency: this.state.currency,
      webhook_events: JSON.stringify(this.state.events),
    };
    METHODS.forEach((m) => (body[`method_${m.k}`] = this.state.methods[m.k] ? 1 : 0));
    WHERE.forEach((w) => (body[`where_${w.k}`] = this.state.where[w.k] ? 1 : 0));

    let payload = await this.call("save", () => saveStripeSettings(this.props.accountno, this.props.token, body));
    if (!payload) return;
    if (wh) {
      const p2 = await this.call("save", () => saveStripeWebhookSecret(this.props.accountno, this.props.token, wh));
      if (!p2) return;
      payload = p2;
      this.setState({ whInput: "", whReplace: false });
    }
    this.applyStatus(payload, payload.message && payload.message.indexOf("Saved") === 0 ? payload.message : "Saved");
  };

  // ── webhook actions ────────────────────────────────────────────────────────

  copyUrl = () => {
    const url = (this.s().webhook || {}).url || "";
    if (navigator.clipboard && url) navigator.clipboard.writeText(url).catch(() => {});
    if (this.props.toast) this.props.toast("Endpoint copied", "success");
  };

  ping = async () => {
    const wh = this.s().webhook || {};
    if (!wh.secret_set && !this.state.whInput.trim()) {
      this.setState({ pingOut: { ok: false, text: "Save a signing secret first." } });
      return;
    }
    const payload = await this.call("ping", () => checkStripeWebhook(this.props.accountno, this.props.token));
    if (!payload) {
      this.setState({ pingOut: { ok: false, text: this.state.error } });
      return;
    }
    this.setState({ pingOut: { ok: !!(payload.data && payload.data.verified), text: payload.message } });
    await this.loadDeliveries();
  };

  loadDeliveries = async () => {
    const payload = await this.call("deliveries", () => getStripeDeliveries(this.props.accountno, this.props.token, 1, 20));
    if (!payload) return;
    this.setState({ deliveries: (payload.data && payload.data.rows) || [], deliveriesLoaded: true });
  };

  setTab = (k) => {
    this.setState({ tab: k, error: "", success: "" });
    if (k === "hooks" && !this.state.deliveriesLoaded) this.loadDeliveries();
  };

  close = () => {
    if (window.$) window.$("#modalStripeIntegration").modal("hide");
    if (this.props.onClose) this.props.onClose();
  };

  // ── shared bits of markup ──────────────────────────────────────────────────

  sectionHead(title, note, cls) {
    return (
      <div className={"sk-sec " + (cls || "")}>
        <div className="sk-sec-t">{title}</div>
        {note ? <div className="form-note">{note}</div> : null}
      </div>
    );
  }

  switchRow(id, label, note, on, onChange, disabled) {
    return (
      <div className="sk-opt" key={id}>
        <div className="sk-opt-txt">
          <label htmlFor={id} className="sk-opt-lbl">{label}</label>
          <div className="form-note">{note}</div>
        </div>
        <div className="custom-control custom-switch">
          <input
            type="checkbox"
            className="custom-control-input"
            id={id}
            checked={!!on}
            disabled={!!disabled}
            onChange={onChange}
          />
          <label className="custom-control-label" htmlFor={id}></label>
        </div>
      </div>
    );
  }

  /** More / Less toggle for a switch list; `key` is the boolean state flag it flips. */
  moreBtn(key) {
    const open = this.state[key];
    return (
      <div className="text-center my-2">
        <button
          type="button"
          className="btn btn-sm btn-primary"
          style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "6px 14px", borderRadius: "999px", fontSize: "12px", fontWeight: 600, boxShadow: "0 3px 10px rgba(0,0,0,0.12)" }}
          onClick={() => this.setState({ [key]: !open })}
        >
          <em className={"icon ni " + (open ? "ni-arrow-up" : "ni-arrow-down")}></em> {open ? "Show less" : "Show more"}
        </button>
      </div>
    );
  }

  outcome(o) {
    if (!o) return null;
    return (
      <span className={"sk-out " + (o.ok ? "text-success" : "text-danger")}>
        <em className={"icon ni " + (o.ok ? "ni-check-circle-fill" : "ni-alert-circle-fill")}></em> {o.text}
      </span>
    );
  }

  // ── render: header ─────────────────────────────────────────────────────────

  renderHeaderSub() {
    const s = this.s();
    if (!s.connected) return "Not connected — enter your Stripe keys to connect";
    if (!s.enabled) return "Connected, but payments are paused — no client can pay";
    return s.mode === "live"
      ? `Live — clients are charged for real · ${s.currency || "USD"}`
      : `Test — no money moves · ${s.currency || "USD"}`;
  }

  renderStateBanner() {
    const s = this.s();
    const live = s.mode === "live";
    const held = this.has(s.mode);
    let tone = "off";
    let icon = "ni-cross-circle";
    let title = "Not connected";
    let note = "Paste the keys from your Stripe dashboard to connect.";
    if (s.connected && held) {
      tone = live ? "live" : "test";
      icon = live ? "ni-check-circle-fill" : "ni-clock";
      title = live ? "Live and taking payments" : "Connected in test mode";
      note =
        this.biz() +
        (live
          ? " · direct charges · every payment is real money."
          : " · direct charges · pay an invoice with a test card to try it end to end.");
    } else if (s.connected) {
      tone = "off";
      icon = "ni-alert-circle";
      title = "No live keys yet";
      note = "Add the live key pair below. The test keys stay where they are.";
    }
    return (
      <div className={"sk-banner sk-banner-" + tone}>
        <em className={"icon ni " + icon}></em>
        <div>
          <div className="sk-banner-t">{title}</div>
          <div className="sk-banner-s">{note}</div>
        </div>
      </div>
    );
  }

  // ── render: connection ─────────────────────────────────────────────────────

  renderConnection() {
    const s = this.s();
    const live = s.mode === "live";
    const hasLive = this.has("live");
    const st = this.state;
    const cur = this.pair(s.mode);
    let hint = "Live keys are held and ready.";
    let hintWarn = false;
    if (!hasLive) hint = "Switching to live needs the live key pair.";
    else if (live) {
      hint = "Every payment is real money.";
      hintWarn = true;
    }

    return (
      <div>
        {this.renderStateBanner()}

        {this.sectionHead("Environment")}
        <div className="sk-opt">
          <div className="sk-opt-txt">
            <div className="sk-opt-lbl">Which keys are in use</div>
            <div className="form-note">
              {live
                ? "Live keys are in use. Clients are charged for real."
                : "Test keys are in use. Nothing is charged — use Stripe's test cards."}
            </div>
          </div>
          <div className="text-right">
            <div className="btn-group btn-group-sm" role="group" aria-label="Stripe environment">
              <button
                type="button"
                className={"btn " + (!live ? "btn-primary" : "btn-outline-light")}
                onClick={() => this.setMode("test")}
                disabled={!!st.busy}
              >
                Test
              </button>
              <button
                type="button"
                className={"btn " + (live ? "btn-success" : "btn-outline-light")}
                onClick={() => this.setMode("live")}
                disabled={!!st.busy}
              >
                Live
              </button>
            </div>
            <div className={"form-note mt-1 " + (hintWarn ? "text-warning" : "")}>{hint}</div>
          </div>
        </div>

        {st.updOpen && (
          <div className="sk-panel">
            <div className="sk-panel-hd">
              <em className="icon ni ni-alert-circle"></em>
              <span>
                {this.has(st.updTarget)
                  ? st.updTarget === "live"
                    ? "These keys are charging people. The connection stays on the current pair until you verify the new one and confirm."
                    : "Replacing the test keys. Nothing that touches real money changes."
                  : `No ${st.updTarget} keys are held yet. Add the pair here, and Stripe switches to ${st.updTarget} once they verify.`}
              </span>
            </div>
            <div className="p-3">
              <div className="form-group">
                <MutextField
                  id="stripe_new_pk"
                  label="New publishable key"
                  placeholder={`pk_${st.updTarget}_…`}
                  value={st.newPk}
                  error={!!st.newPkErr}
                  helperText={st.newPkErr || " "}
                  onChange={(e) => this.setState({ newPk: e.target.value, verified: false, verifyOut: null, newPkErr: "" })}
                  variant="outlined"
                  fullWidth
                  autoComplete="new-password"
                />
              </div>
              <div className="form-group mt-2">
                <MutextField
                  id="stripe_new_sk"
                  type="password"
                  label="New secret key"
                  placeholder={`sk_${st.updTarget}_…`}
                  value={st.newSk}
                  error={!!st.newSkErr}
                  helperText={st.newSkErr || "Typed once. It is stored and shown back only as a mask."}
                  onChange={(e) => this.setState({ newSk: e.target.value, verified: false, verifyOut: null, newSkErr: "" })}
                  variant="outlined"
                  fullWidth
                  autoComplete="new-password"
                />
              </div>
              <div className="sk-actions">
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={this.verify} disabled={st.busy === "verify"}>
                  <em className="icon ni ni-shield-check mr-1"></em>
                  {st.busy === "verify" ? "Verifying..." : "Verify new keys"}
                </button>
                {this.outcome(st.verifyOut)}
                <div className="ml-auto d-flex" style={{ gap: 8 }}>
                  <button type="button" className="btn btn-sm btn-light" onClick={this.closeUpdate}>Cancel</button>
                  <button type="button" className="btn btn-sm btn-primary" onClick={this.applyKeys} disabled={!st.verified || st.busy === "update"}>
                    {st.busy === "update" ? "Updating..." : "Update keys"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {s.connected ? (
          <>
            {this.sectionHead(`API keys — ${live ? "live" : "test"}`)}
            <div className="sk-keys">
              <div className="sk-key-row">
                <div className="sk-key-lbl">
                  Publishable key
                  <div className="form-note">Public by design — it ships to the browser in the payment form.</div>
                </div>
                <div className="sk-key-val">{cur.publishable_key || "—"}</div>
              </div>
              <div className="sk-key-row">
                <div className="sk-key-lbl">
                  Secret key
                  <div className="form-note">Held server-side. There is no way to read it back from here, by design.</div>
                </div>
                <div className="sk-key-val">{cur.secret_key_mask || "—"}</div>
              </div>
              <div className="sk-actions">
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={this.test} disabled={st.busy === "test"}>
                  <em className="icon ni ni-signal mr-1"></em>
                  {st.busy === "test" ? "Testing..." : "Test connection"}
                </button>
                <button type="button" className="btn btn-sm btn-outline-light" onClick={() => this.openUpdate()}>
                  <em className="icon ni ni-edit mr-1"></em> Update keys
                </button>
                {this.outcome(st.testOut)}
              </div>
            </div>
          </>
        ) : (
          <>
            {this.sectionHead("Connect your Stripe account")}
            <div className="sk-steps">
              {["Paste your keys", "Register the webhook", "Take a test payment", "Switch to live"].map((t, i) => (
                <span className="sk-step" key={t}>
                  <b>{i + 1}</b>
                  {t}
                </span>
              ))}
            </div>
            <div className="form-group mt-3">
              <MutextField
                id="stripe_connect_pk"
                label="Publishable key"
                placeholder={`pk_${s.mode || "test"}_…`}
                value={st.cpk}
                error={!!st.cpkErr}
                helperText={st.cpkErr || " "}
                onChange={(e) => this.setState({ cpk: e.target.value, cpkErr: "", connOut: null })}
                variant="outlined"
                fullWidth
                autoComplete="new-password"
              />
            </div>
            <div className="form-group mt-2">
              <MutextField
                id="stripe_connect_sk"
                type="password"
                label="Secret key"
                placeholder={`sk_${s.mode || "test"}_…`}
                value={st.csk}
                error={!!st.cskErr}
                helperText={st.cskErr || "Stored encrypted. It is never shown again."}
                onChange={(e) => this.setState({ csk: e.target.value, cskErr: "", connOut: null })}
                variant="outlined"
                fullWidth
                autoComplete="new-password"
              />
            </div>
            <div className="sk-actions">
              <button type="button" className="btn btn-primary" onClick={this.connect} disabled={st.busy === "connect"}>
                <em className="icon ni ni-link-alt mr-1"></em>
                {st.busy === "connect" ? "Connecting..." : "Connect Stripe"}
              </button>
              {this.outcome(st.connOut)}
            </div>
          </>
        )}
      </div>
    );
  }

  // ── render: settings ───────────────────────────────────────────────────────

  renderSettings() {
    const s = this.s();
    const st = this.state;
    return (
      <div>
        {this.sectionHead("Stripe payments")}
        {this.switchRow(
          "stripe_enabled",
          "Accept payments right now",
          "On — clients see a Pay button on any unpaid invoice. Off — Stripe stays connected and configured, but no client can pay.",
          s.enabled,
          this.toggleEnabled
        )}
        <div className={"sk-state-note " + (s.enabled ? "on" : "off")}>
          <em className={"icon ni " + (s.enabled ? "ni-check-circle" : "ni-pause-circle")}></em>{" "}
          {s.enabled ? "On — clients can pay their invoices" : "Off — the Pay button is hidden from every client"}
        </div>

        {this.sectionHead("What clients can pay with", null, "sk-sec-lg")}
        {(st.moreMethods ? METHODS : METHODS.slice(0, 2)).map((m) =>
          this.switchRow(
            "stripe_m_" + m.k,
            m.n,
            m.s,
            m.locked ? true : st.methods[m.k],
            () => this.flipMethod(m.k),
            m.locked
          )
        )}
        {this.moreBtn("moreMethods")}

        {this.sectionHead("Where Stripe is offered", null, "sk-sec-lg")}
        {(st.moreWhere ? WHERE : WHERE.slice(0, 2)).map((w) =>
          this.switchRow("stripe_w_" + w.k, w.n, w.s, st.where[w.k], () => this.flipWhere(w.k))
        )}
        {this.moreBtn("moreWhere")}

        {this.sectionHead("Stripe customers")}
        {this.switchRow(
          "stripe_auto_customer",
          "Create a Stripe customer on first payment",
          "On — the client gets a Stripe customer record, which is what lets a card be kept on file and auto-charge work. Off — every payment is a one-off charge and nothing can be saved.",
          st.autoCustomer,
          () => this.setState({ autoCustomer: !st.autoCustomer })
        )}
        <div className={"sk-state-note " + (st.autoCustomer ? "on" : "off")}>
          <em className={"icon ni " + (st.autoCustomer ? "ni-check-circle" : "ni-pause-circle")}></em>{" "}
          {st.autoCustomer ? "On — a card can be kept on file" : "Off — one-off charges only, nothing can be saved"}
        </div>

        {this.sectionHead("On the statement")}
        <div className="row">
          <div className="col-sm-7">
            <div className="form-group mt-2">
              <MutextField
                id="stripe_descriptor"
                label="Statement descriptor"
                placeholder="ONBOARDSOFT"
                value={st.descriptor}
                error={st.descErr}
                inputProps={{ maxLength: 22 }}
                helperText={
                  st.descErr
                    ? "Set a statement descriptor."
                    : `${st.descriptor.length}/22 · shown on the client's card statement`
                }
                onChange={(e) => this.setState({ descriptor: e.target.value, descErr: false })}
                variant="outlined"
                fullWidth
                autoComplete="new-password"
              />
            </div>
          </div>
          <div className="col-sm-5">
            <div className="form-group mt-2">
              <MutextField
                id="stripe_currency"
                select
                label="Currency"
                value={st.currency}
                onChange={(e) => this.setState({ currency: e.target.value })}
                SelectProps={{ native: true }}
                variant="outlined"
                fullWidth
                helperText=" "
              >
                {CURRENCIES.map((c) => (
                  <option key={c.v} value={c.v}>
                    {c.t}
                  </option>
                ))}
              </MutextField>
            </div>
          </div>
        </div>
        <div className="form-note">
          What a client sees on their card statement. Something they will not recognise is the most common cause of a chargeback.
        </div>

        {this.sectionHead("When a payment succeeds", "What the portal does. Not configurable.")}
        <ul className="sk-fixed">
          <li>
            <em className="icon ni ni-check-circle-fill"></em>
            <span>
              <b>The invoice is updated</b>
              <span className="form-note">Paid in full marks it Paid; anything less marks it Partial.</span>
            </span>
          </li>
          <li>
            <em className="icon ni ni-check-circle-fill"></em>
            <span>
              <b>A payment is recorded</b>
              <span className="form-note">Method, amount, date and the Stripe charge reference, on the invoice.</span>
            </span>
          </li>
          <li>
            <em className="icon ni ni-check-circle-fill"></em>
            <span>
              <b>Both portals are told</b>
              <span className="form-note">A payment notice for the client, and one for the admin.</span>
            </span>
          </li>
        </ul>
      </div>
    );
  }

  // ── render: webhooks ───────────────────────────────────────────────────────

  renderHooks() {
    const s = this.s();
    const wh = s.webhook || {};
    const st = this.state;
    const catalog = s.event_catalog || [];
    const showInput = !wh.secret_set || st.whReplace;
    return (
      <div>
        {this.sectionHead("Endpoint", "Add this in Stripe under Developers → Webhooks, then paste the signing secret below.")}
        <div className="form-group">
          <label className="form-label">URL</label>
          <div className="form-control-wrap d-flex" style={{ gap: 8 }}>
            <input className="form-control sk-mono" readOnly value={wh.url || ""} />
            <button type="button" className="btn btn-outline-light" onClick={this.copyUrl} style={{ flexShrink: 0 }}>
              <em className="icon ni ni-copy"></em>
            </button>
          </div>
        </div>

        <div className="form-group mt-3">
          {!showInput ? (
            <>
              <label className="form-label">Signing secret</label>
              <div className="sk-key-row sk-key-row-flat">
                <div className="sk-key-val flex-grow-1">{wh.secret_mask || "••••"}</div>
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => this.setState({ whReplace: true })}>
                  Replace
                </button>
              </div>
            </>
          ) : (
            <MutextField
              id="stripe_whsec"
              type="password"
              label="Signing secret"
              placeholder="whsec_…"
              value={st.whInput}
              error={st.whErr}
              helperText={st.whErr ? "That does not look like a signing secret." : "Stripe shows this once, when the endpoint is created."}
              onChange={(e) => this.setState({ whInput: e.target.value, whErr: false })}
              variant="outlined"
              fullWidth
              autoComplete="new-password"
            />
          )}
        </div>

        <div className="sk-actions">
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={this.ping} disabled={st.busy === "ping"}>
            <em className="icon ni ni-send mr-1"></em>
            {st.busy === "ping" ? "Checking..." : "Send a test event"}
          </button>
          {this.outcome(st.pingOut)}
        </div>

        <div className={"alert alert-pro mt-2 " + (wh.verified ? "alert-success" : "alert-warning")}>
          <div className="alert-text">
            <p className="mb-0">
              {wh.verified
                ? "Verified — the last event arrived and its signature matched."
                : "Not verified. Send a test event once the endpoint is registered in Stripe."}
            </p>
          </div>
        </div>

        {this.sectionHead("Events to receive", "Stripe only sends what you subscribe to.")}
        <div className="sk-events">
          {catalog.map((e) => (
            <div className="sk-event" key={e.k}>
              {/* The theme lays .custom-control out as an inline-flex box with no
                  label padding, so the text sits in its own label beside it —
                  the same split the SIPNav switches use. */}
              <div className="custom-control custom-checkbox">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id={"stripe_ev_" + e.k}
                  checked={st.events.includes(e.k)}
                  onChange={() => this.flipEvent(e.k)}
                />
                <label className="custom-control-label" htmlFor={"stripe_ev_" + e.k}></label>
              </div>
              <label className="sk-event-txt" htmlFor={"stripe_ev_" + e.k}>
                <span className="sk-mono">{e.k}</span>
                <span className="form-note">{e.s}</span>
              </label>
            </div>
          ))}
        </div>

        {this.sectionHead("Recent deliveries")}
        {st.deliveries.length ? (
          <div className="table-responsive">
            <table className="table table-sm sk-table">
              <thead>
                <tr>
                  <th>Event</th>
                  <th>What the portal did</th>
                  <th>Result</th>
                  <th className="text-right">When</th>
                </tr>
              </thead>
              <tbody>
                {st.deliveries.map((d) => {
                  const ok = d.status === "processed" || d.status === "ignored";
                  const failed = d.status === "failed";
                  return (
                    <tr key={d.id}>
                      <td className="sk-mono">{d.event_type}</td>
                      <td>{d.action_taken || (d.status === "processing" ? "Processing…" : d.error_message || "—")}</td>
                      <td>
                        <span className={"badge badge-dot badge-dim badge-" + (ok ? "success" : failed ? "danger" : "warning")}>
                          {failed ? "500 Failed" : ok ? "200 OK" : "Pending"}
                        </span>
                      </td>
                      <td className="text-right text-soft">{ago(d.processed_at || d.received_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="sk-empty">{st.deliveriesLoaded ? "Nothing delivered yet." : "Loading…"}</div>
        )}
      </div>
    );
  }

  render() {
    const s = this.s();
    const st = this.state;
    const tabs = [
      { k: "conn", t: "Connection", i: "ni-link-alt" },
      { k: "settings", t: "Settings", i: "ni-setting-alt" },
      { k: "hooks", t: "Webhooks", i: "ni-swap-alt" },
    ];
    return (
      <div className="modal fade" tabIndex="-1" id="modalStripeIntegration" role="dialog" aria-labelledby="modalStripeTitle">
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg" role="document">
          <div className="modal-content">
            {/* Header */}
            <div className="modal-header" style={{ borderBottom: "1px solid #e5e9f2", padding: "14px 20px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: "#f0f3ff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <em className="icon ni ni-cc-alt2" style={{ color: "#6576ff", fontSize: 20 }}></em>
                </div>
                <div>
                  <div id="modalStripeTitle" style={{ color: "#364a63", fontWeight: 600, fontSize: "1rem" }}>
                    Stripe Integration
                  </div>
                  <div style={{ color: "#8094ae", fontSize: "0.8rem" }}>{this.renderHeaderSub()}</div>
                </div>
              </div>
              <a style={{ cursor: "pointer" }} className="close" onClick={this.close}>
                <em className="icon ni ni-cross"></em>
              </a>
            </div>

            {/* Tabs */}
            <ul className="nav nav-tabs nav-tabs-s2 px-3 pt-2" style={{ background: "#f8f9ff", borderBottom: "1px solid #e5e9f2" }}>
              {tabs.map((t) => (
                <li className="nav-item" key={t.k} style={{ cursor: "pointer" }}>
                  <a className={"nav-link" + (st.tab === t.k ? " active" : "")} onClick={() => this.setTab(t.k)}>
                    <em className={"icon ni " + t.i}></em> <span>{t.t}</span>
                  </a>
                </li>
              ))}
            </ul>

            <div className="modal-body modal-body-md p-3">
              {st.error ? (
                <div className="alert alert-pro alert-danger mb-3">
                  <div className="alert-text">
                    <h6>Error</h6>
                    <p>{st.error}</p>
                  </div>
                </div>
              ) : null}
              {st.tab === "conn" && this.renderConnection()}
              {st.tab === "settings" && this.renderSettings()}
              {st.tab === "hooks" && this.renderHooks()}
            </div>

            {/* Footer */}
            <div className="modal-footer" style={{ borderTop: "1px solid #e5e9f2", padding: "12px 20px", gap: 10, justifyContent: "flex-end" }}>
              {s.connected && (
                <Tooltip title="Remove the stored keys and stop taking payments" placement="top">
                  <button
                    type="button"
                    className="btn btn-outline-danger mr-auto"
                    onClick={this.disconnect}
                    disabled={st.busy === "disconnect"}
                  >
                    <em className="icon ni ni-cross-circle mr-1"></em>
                    {st.busy === "disconnect" ? "Disconnecting..." : "Disconnect"}
                  </button>
                </Tooltip>
              )}
              <button type="button" className="btn btn-light" onClick={this.close}>
                Close
              </button>
              <button type="button" className="btn btn-primary" onClick={this.save} disabled={st.busy === "save"}>
                {st.busy === "save" ? (
                  <span>
                    <em className="icon ni ni-loader mr-1"></em> Saving...
                  </span>
                ) : (
                  <span>
                    <em className="icon ni ni-check mr-1"></em> Save Changes
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default StripeModal;
